import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, Trophy, Target, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const GOALS = [30, 60, 90, 120];
const weekKey = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); };

const GoalRing = ({ value, goal }) => {
  const pct = Math.min(1, value / goal), r = 34, c = 2 * Math.PI * r;
  return (
    <svg width="84" height="84" viewBox="0 0 84 84" data-testid="practice-goal-ring">
      <circle cx="42" cy="42" r={r} stroke="hsl(var(--secondary))" strokeWidth="8" fill="none" />
      <circle cx="42" cy="42" r={r} stroke="hsl(var(--primary))" strokeWidth="8" fill="none" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 42 42)" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      <text x="42" y="47" textAnchor="middle" fontSize="15" fontWeight="700" fill="hsl(var(--foreground))">{Math.round(pct * 100)}%</text>
    </svg>
  );
};
import { getLesson, getLessonContext } from "@/data/curriculum";

export const PracticeLog = () => {
  const [data, setData] = useState(null);
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    api.get("/practice/summary").then(({ data: d }) => setData(d)).catch(() => {});
    api.get("/practice/leaderboard").then(({ data: d }) => setLeaders(d.leaders || [])).catch(() => {});
  }, []);
  const smashed = data?.goal && data.practice_week_minutes >= data.goal;
  useEffect(() => {
    if (!smashed) return;
    const k = `jt_goal_smashed_${weekKey()}`;
    if (!localStorage.getItem(k)) { localStorage.setItem(k, "1"); toast.success(`Goal smashed! ${data.goal} minutes of practice this week.`); }
  }, [smashed, data]);
  useEffect(() => {
    if (!data || data.practice_streak < 5) return;
    const k = `jt_streak5_${new Date().toISOString().slice(0, 10)}`;
    if (!localStorage.getItem(k)) { localStorage.setItem(k, "1"); toast.success(`🔥 ${data.practice_streak}-day practice streak! Keep it going.`); }
  }, [data]);
  if (!data) return null;
  const setGoal = async (m) => {
    try { await api.put("/practice/goal", { minutes: m }); setData({ ...data, goal: m }); } catch { toast.error("Could not save goal"); }
  };

  return (
    <section className="mt-12 rounded-2xl border border-border bg-card p-6" data-testid="practice-log">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Headphones size={14} /> Play-along practice</p>
          <h2 className="mt-1 font-serif text-2xl">Practice log</h2>
        </div>
        <div className="flex gap-6 text-right">
          <div><p className="font-serif text-3xl text-foreground" data-testid="practice-week-minutes">{data.practice_week_minutes}</p><p className="text-xs text-muted-foreground">minutes this week</p></div>
          <div><p className="font-serif text-3xl text-foreground" data-testid="practice-total-minutes">{data.practice_total_minutes}</p><p className="text-xs text-muted-foreground">minutes in total</p></div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3 text-sm" data-testid="practice-streak">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5"><Flame size={14} className="text-primary" /> Current streak: <strong data-testid="practice-streak-current">{data.practice_streak}</strong> day{data.practice_streak === 1 ? "" : "s"}</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5">Best: <strong data-testid="practice-streak-best">{data.best_streak}</strong></span>
        {data.best_streak >= 5 && <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground" data-testid="streak-5-badge"><Flame size={12} /> 5-day streak</span>}
        {data.practice_streak > 0 && data.practice_streak < 5 && <span className="text-xs text-muted-foreground">{5 - data.practice_streak} more day{5 - data.practice_streak === 1 ? "" : "s"} for the 5-day streak badge</span>}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-5 rounded-xl bg-secondary/60 p-4" data-testid="practice-goal">
        {data.goal ? <GoalRing value={data.practice_week_minutes} goal={data.goal} /> : <Target size={36} className="text-primary" />}
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">{data.goal ? `Weekly goal: ${data.practice_week_minutes} / ${data.goal} min` : "Set a weekly practice goal"}</p>
          {smashed && <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground" data-testid="goal-smashed-badge"><Trophy size={12} /> Goal smashed!</p>}
          <div className="mt-2 flex gap-2">
            {GOALS.map((m) => (
              <button key={m} onClick={() => setGoal(m)} data-testid={`practice-goal-${m}`}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${data.goal === m ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}>{m} min</button>
            ))}
          </div>
        </div>
      </div>
      {data.lessons.length ? (
        <ul className="mt-5 divide-y divide-border" data-testid="practice-lessons">
          {data.lessons.slice(0, 8).map((l) => {
            const lesson = getLesson(l.lesson_id);
            const ctx = getLessonContext(l.lesson_id);
            if (!lesson) return null;
            return (
              <li key={l.lesson_id} className="flex items-center justify-between py-2.5 text-sm">
                <Link to={`/lessons/${l.lesson_id}`} className="text-foreground hover:text-primary">{ctx?.instrument.label} · Lesson {ctx?.number}: {lesson.title}</Link>
                <span className="font-mono text-xs text-primary">{l.minutes} min</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">No play-along practice yet — open lesson 4 or later and press Play.</p>
      )}
      {leaders.length > 0 && (
        <div className="mt-6" data-testid="practice-leaderboard">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Trophy size={13} /> Top practice stars this week</p>
          <ol className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {leaders.map((l) => (
              <li key={l.rank} className="flex items-center justify-between rounded-lg bg-background px-3 py-2 text-sm" data-testid={`practice-leader-${l.rank}`}>
                <span><span className="mr-2 font-mono text-xs text-primary">#{l.rank}</span>{l.name}</span><span className="font-mono text-xs">{l.minutes} min</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
};
